/**
 * Org Admin route definitions (/admin/org/*)
 * Extracted from Router.tsx to keep each file under 500 lines
 */

import React, { Suspense } from 'react'

import { Route, Navigate } from 'react-router-dom'

import { ProtectedRoute } from '@/platform/auth/components/ProtectedRoute'
import { RouteErrorBoundary } from '@/shared/components/RouteErrorBoundary'

import { LoadingSpinner } from './LoadingSpinner'
import {
  OrgAdminLayout,
  OrgDashboardPage,
  OrgUsersPage,
  OrgSettingsPage,
  OrgBillingPage,
  OrgAccountantsPage,
  OrgExportPage,
  OrgReportsPage,
  OrgActivityPage,
  OrgApiKeysPage,
  OrgPlatformApiKeysPage,
  WebhooksPage,
  PermissionsManagementPage,
  OrgJobsPage,
  SsoSettingsPage,
  DataRetentionPage,
  TeamsPage,
  OrgRolesPage,
  OrgNotificationsPage,
} from './routes'

/**
 * Org Admin routes - accessible by org_admin role
 * Renders as a nested route under /admin/org
 */
export const OrgAdminRoutes = () => (
  <Route
    path="/admin/org"
    element={
      <ProtectedRoute fallback="/login" requiredRoles={['org_admin']}>
        <Suspense fallback={<LoadingSpinner />}>
          <OrgAdminLayout />
        </Suspense>
      </ProtectedRoute>
    }
  >
    <Route index element={<Navigate to="dashboard" replace />} />
    <Route
      path="dashboard"
      element={
        <RouteErrorBoundary routeName="org-dashboard">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgDashboardPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="users"
      element={
        <RouteErrorBoundary routeName="org-users">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgUsersPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="settings"
      element={
        <RouteErrorBoundary routeName="org-settings">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgSettingsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="billing"
      element={
        <RouteErrorBoundary routeName="org-billing">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgBillingPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="accountants"
      element={
        <RouteErrorBoundary routeName="org-accountants">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgAccountantsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="export"
      element={
        <RouteErrorBoundary routeName="org-export">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgExportPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="reports"
      element={
        <RouteErrorBoundary routeName="org-reports">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgReportsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="activity"
      element={
        <RouteErrorBoundary routeName="org-activity">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgActivityPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* API Keys */}
    <Route
      path="api-keys"
      element={
        <RouteErrorBoundary routeName="org-api-keys">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgApiKeysPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Platform API Keys */}
    <Route
      path="platform-api-keys"
      element={
        <RouteErrorBoundary routeName="org-platform-api-keys">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgPlatformApiKeysPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Webhooks */}
    <Route
      path="webhooks"
      element={
        <RouteErrorBoundary routeName="org-webhooks">
          <Suspense fallback={<LoadingSpinner />}>
            <WebhooksPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Permissions */}
    <Route
      path="permissions"
      element={
        <RouteErrorBoundary routeName="org-permissions">
          <Suspense fallback={<LoadingSpinner />}>
            <PermissionsManagementPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Jobs */}
    <Route
      path="jobs"
      element={
        <RouteErrorBoundary routeName="org-jobs">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgJobsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* SSO Settings */}
    <Route
      path="sso"
      element={
        <RouteErrorBoundary routeName="org-sso">
          <Suspense fallback={<LoadingSpinner />}>
            <SsoSettingsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Data Retention */}
    <Route
      path="data-retention"
      element={
        <RouteErrorBoundary routeName="org-data-retention">
          <Suspense fallback={<LoadingSpinner />}>
            <DataRetentionPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Teams */}
    <Route
      path="teams"
      element={
        <RouteErrorBoundary routeName="org-teams">
          <Suspense fallback={<LoadingSpinner />}>
            <TeamsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Org Roles */}
    <Route
      path="roles"
      element={
        <RouteErrorBoundary routeName="org-roles">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgRolesPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Org Notifications */}
    <Route
      path="notifications"
      element={
        <RouteErrorBoundary routeName="org-notifications">
          <Suspense fallback={<LoadingSpinner />}>
            <OrgNotificationsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
  </Route>
)
